import { useParams, useSearchParams } from 'react-router-dom';

import type {
  MembersTableCoachCountFilter,
  MembersTableParams,
} from '@/store/api/userApi';
import type { SeatType } from '@/types';

import {
  liveCohortFilter,
  SESSION_FILTERS,
  useMembersFilterParams,
} from '@/features/companyDashboard/common/members/useMembersFilterParams';
import { useGetCompanyCohortTagsQuery } from '@/store/api/cohortTagApi';

export type CompanyMembersPage = 'partner' | 'ops';

export const MEMBERSHIP_KEY = 'membership';
export const MEMBER_STATUSES_KEY = 'member_statuses';
export const END_DATE_STATUSES_KEY = 'end_date_statuses';
export const INSIGHTS_STATUSES_KEY = 'insights_statuses';
export const SEAT_TYPES_KEY = 'seat_types';
export const COHORT_TAG_IDS_KEY = 'cohort_tag_ids';
export const COACH_IDS_KEY = 'coach_ids';
export const COACHES_ASSIGNED_KEY = 'coaches_assigned';

export const MEMBERSHIP_OPTIONS = [
  { id: 'current', name: 'Current' },
  { id: 'past', name: 'Past' },
];

// An empty Membership selection is stored as `membership=none`, because a missing key means
// the default, Current.
const NO_MEMBERSHIP = 'none';

export const membershipSelection = (values: string[]) => {
  if (values.includes(NO_MEMBERSHIP)) return [];

  const selected = values.filter((value) =>
    MEMBERSHIP_OPTIONS.some((option) => option.id === value),
  );
  return selected.length ? selected : ['current'];
};

export const membershipParamValues = (selected: string[]) =>
  selected.length ? selected : [NO_MEMBERSHIP];

// Undefined when nothing is selected: callers skip the members request.
const companyMemberStatusFor = (
  selected: string[],
): MembersTableParams['companyMemberStatus'] => {
  const current = selected.includes('current');
  const past = selected.includes('past');
  if (current && past) return 'all';
  if (past) return 'past';
  if (current) return 'current';
  return undefined;
};

// The one source of a company page's member filters, shared by its table and its CSV export so
// the file lists exactly the members on screen. Search is partner-only until MEN-5506.
export const useCompanyMembersParams = (
  page: CompanyMembersPage,
): MembersTableParams => {
  const { companyId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { arrayFilter } = useMembersFilterParams();
  const { data: cohortTags = [], isSuccess: cohortTagsLoaded } =
    useGetCompanyCohortTagsQuery(companyId, { skip: !companyId });

  const sessionFilter = SESSION_FILTERS.filter(
    (filter) => searchParams.get(filter) === 'true',
  );
  const params: MembersTableParams = {
    chemCall: sessionFilter.includes('chem_call') || undefined,
    cohortTagIds: liveCohortFilter(
      arrayFilter(COHORT_TAG_IDS_KEY),
      cohortTags,
      cohortTagsLoaded,
    ),
    companyIds: [companyId],
    companyMemberStatus: companyMemberStatusFor(
      membershipSelection(arrayFilter(MEMBERSHIP_KEY)),
    ),
    endDateStatuses: arrayFilter(END_DATE_STATUSES_KEY),
    insightsStatuses: arrayFilter(INSIGHTS_STATUSES_KEY),
    memberStatuses: arrayFilter(MEMBER_STATUSES_KEY),
    noUpcomingSessions:
      sessionFilter.includes('no_upcoming_sessions') || undefined,
    scheduledSessions:
      sessionFilter.includes('scheduled_sessions') || undefined,
    seatTypes: arrayFilter(SEAT_TYPES_KEY) as SeatType[],
  };

  if (page === 'ops') {
    return {
      ...params,
      coachIds: arrayFilter(COACH_IDS_KEY),
      coachesAssigned: arrayFilter(
        COACHES_ASSIGNED_KEY,
      ) as MembersTableCoachCountFilter[],
    };
  }

  return {
    ...params,
    search: searchParams.get('search') || undefined,
  };
};
